import * as React from "react";
import { useMemo, useState, memo } from "react";
import { motion, useMotionValue } from "framer-motion";
import { useIconTransform } from "./use-icon-transform";

// Memoized Item component to prevent unnecessary re-renders
export const Item = memo(function Item({ row, col, planeX, planeY, label, isMood, moodImage, selected, onClick, iconSize, iconMargin }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  const [isExploding, setIsExploding] = useState(false);

  // Generate a stable random gradient that doesn't change on re-renders
  const backgroundGradient = useMemo(() => {
    const hue = Math.random() * 360;
    const lighterShade = `hsla(${hue}, 95%, 65%, 1)`;
    const darkerShade = `hsla(${hue}, 95%, 40%, 1)`;
    return `linear-gradient(135deg, ${lighterShade}, ${darkerShade})`;
  }, []);

  // Generate background style - vibrant gradient for moods, random gradient for genres
  const backgroundStyle = useMemo(() => {
    if (moodImage) {
      // Mood circles: use image as background
      return `url(${moodImage})`;
    }
    // Genre circles: random gradient background
    return backgroundGradient;
  }, [moodImage, backgroundGradient]);

  // Handle click with explosion animation
  const handleClick = () => {
    setIsExploding(true);
    onClick();
    setTimeout(() => setIsExploding(false), 1000);
  };

  // Calculate the origin x and y offsets of this icon based on
  // its column and row position, using dynamic iconSize and iconMargin
  const xOffset =
    col * (iconSize + iconMargin) +
    (row % 2) * ((iconSize + iconMargin) / 2);
  const yOffset = row * iconSize;

  // Transform the icon's x, y and scale based on the position of the draggable plane
  useIconTransform({ x, y, scale, planeX, planeY, xOffset, yOffset });

  return (
    <>
      <motion.div
        onClick={handleClick}
        style={{
          position: "absolute",
          left: xOffset,
          top: yOffset,
          x,
          y,
          scale,
          width: iconSize,
          height: iconSize,
          borderRadius: "50%",
          contain: "strict",
          background: backgroundStyle,
          backgroundSize: moodImage ? "150%" : "auto",
          backgroundPosition: moodImage ? "center" : "initial",
          backgroundRepeat: "no-repeat",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: `${(iconSize / 100) * 1.5}rem`,
          fontWeight: "bold",
          color: "#ffffff",
          textShadow: isMood
            ? "0 0 8px rgba(0, 0, 0, 1), 1px 1px 2px rgba(0, 0, 0, 1), -1px -1px 2px rgba(0, 0, 0, 1), 1px -1px 2px rgba(0, 0, 0, 1), -1px 1px 2px rgba(0, 0, 0, 1)"
            : "0 0 6px rgba(0, 0, 0, 0.9), 1px 1px 2px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8)",
          textAlign: "center",
          padding: "0.5rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          boxShadow: isMood ? "0 0 0 0 rgba(255, 255, 255, 0.7)" : "none",
          border: selected ? "3px solid #FFD700" : "none",
          cursor: "pointer",
          boxSizing: "border-box"
        }}
        animate={isMood ? {
          boxShadow: [
            "0 0 0 0 rgba(255, 255, 255, 0.7)",
            "0 0 0 10px rgba(255, 255, 255, 0)",
            "0 0 0 0 rgba(255, 255, 255, 0)"
          ]
        } : {}}
        transition={isMood ? {
          duration: 2,
          repeat: Infinity,
          ease: "easeOut"
        } : {}}
      >
        {label}
      </motion.div>

      {isExploding && (
        <>
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const colors = ['#FFD700', '#FFA500', '#FF6347', '#00CED1', '#FF1493', '#7FFF00', '#ef4444', '#3b82f6'];
            return (
              <motion.div
                key={i}
                initial={{
                  x: 0,
                  y: 0,
                  scale: 1,
                  opacity: 1
                }}
                animate={{
                  x: Math.cos(angle) * 80,
                  y: Math.sin(angle) * 80,
                  scale: 0,
                  opacity: 0
                }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut"
                }}
                style={{
                  position: "absolute",
                  left: xOffset + iconSize / 2,
                  top: yOffset + iconSize / 2,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: colors[i % colors.length],
                  pointerEvents: "none",
                  zIndex: 1000
                }}
              />
            );
          })}
        </>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  return (
    prevProps.row === nextProps.row &&
    prevProps.col === nextProps.col &&
    prevProps.label === nextProps.label &&
    prevProps.isMood === nextProps.isMood &&
    prevProps.moodImage === nextProps.moodImage &&
    prevProps.selected === nextProps.selected &&
    prevProps.iconSize === nextProps.iconSize &&
    prevProps.iconMargin === nextProps.iconMargin
    // Note: planeX, planeY, and onClick are intentionally not compared
    // planeX/planeY are motion values (stable references), onClick is a callback
  );
});
